import {
  ConfigCatProvider,
  LogLevel,
  User,
  createConsoleLogger,
  useFeatureFlag as useConfigCatFeatureFlag,
} from "configcat-react";
import { useEffect } from "react";

import { isLocal } from "@/config/env";
import { useAnalytics } from "@/hooks/analytics";
import { useAuthState } from "@/hooks/useAuthState";
// Add to `featureFlagOverrides.json` to override what's set in ConfigCat.
// It will be made available to the backend via a docker volume mount.
import FEATURE_FLAG_OVERRIDES from "../../featureFlagOverrides.json";

/**
 * Dependent providers:
 * - AnalyticsProvider
 */
export default function FeatureFlagProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const logger = isLocal ? createConsoleLogger(LogLevel.Info) : undefined;

  return (
    <ConfigCatProvider
      sdkKey={process.env.EXPO_PUBLIC_CONFIGCAT_SDK_KEY!}
      options={{ logger }}
    >
      {children}
    </ConfigCatProvider>
  );
}

/**
 * All feature flags are evaluated locally on both the frontend and backend.
 * When adding a field, also add it to the `UserShapeForConfigCat` class on the backend and
 * pass to the `useFeatureFlag` function.
 */
interface UserShapeForConfigCat {
  identifier: string;
}

export const useFeatureFlag = (key: string, defaultValue?: any) => {
  const { userId, isSignedIn, anonymousUserId, isInitializing } =
    useAuthState();
  const { track } = useAnalytics();
  const configCatValue = useConfigCatFeatureFlag(key, defaultValue, {
    identifier: isSignedIn ? userId : anonymousUserId,
  } as UserShapeForConfigCat as User);

  useEffect(() => {
    if (isInitializing || configCatValue.loading) return;

    track("superclinical-__feature-flag-evaluated", {
      event_context: {
        flag_key: key,
        flag_value: configCatValue.value,
      },
    });
  }, [
    configCatValue.value,
    configCatValue.loading,
    key,
    track,
    isInitializing,
  ]);

  if (isLocal && key in FEATURE_FLAG_OVERRIDES) {
    // @ts-expect-error
    return { value: FEATURE_FLAG_OVERRIDES[key], loading: false };
  }

  // we need to wait for the anon userId before returning the configCatValue
  // because in the case of a/b testing if the anon userId is undefined it will get the same value every time
  // skewing the buckets
  if (isInitializing) {
    return { value: defaultValue, loading: true };
  }

  return configCatValue;
};
